import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Check, X } from "lucide-react";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setBooking(data.booking);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState("pending");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 grain relative">
      <div className="max-w-lg w-full relative z-10">
        {state === "checking" && (
          <div className="text-center" data-testid="success-checking">
            <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto" />
            <p className="text-white/60 mt-6 uppercase tracking-[0.2em] text-sm">Confirming payment…</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="success-confirmed"
          >
            <div className="w-16 h-16 bg-[#D4AF37] flex items-center justify-center mb-8">
              <Check className="w-9 h-9 text-black" strokeWidth={3} />
            </div>
            <span className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em]">Confirmed</span>
            <h1 className="font-display font-bold uppercase tracking-tighter text-5xl md:text-6xl mt-3 leading-[0.9] text-white">
              You're booked.
            </h1>
            <p className="text-white/60 mt-5 font-body">
              A confirmation is on its way to your inbox. Here's your session:
            </p>
            {booking && (
              <div className="mt-8 border border-white/10 divide-y divide-white/10" data-testid="success-details">
                <Detail label="Athlete" value={booking.name} />
                <Detail label="Discipline" value={booking.session_type?.toUpperCase()} />
                <Detail
                  label="Date"
                  value={booking.date ? format(parseISO(booking.date), "EEEE, dd MMM yyyy") : "—"}
                />
                <Detail label="Time" value={`${booking.time} · 60 min`} />
                <Detail label="Plan" value={booking.plan === "monthly" ? "Monthly" : "Single session"} />
                <Detail
                  label="Paid"
                  value={`£${(booking.amount ?? 0).toFixed(2)}${booking.plan === "monthly" ? " / mo" : ""}`}
                  accent
                />
              </div>
            )}
            <p className="text-white/40 text-xs mt-6 max-w-sm" data-testid="cancellation-note">
              Need to cancel or reschedule? Email us at least 24 hours before your session for a full refund or a free reschedule.
            </p>
            <Link
              to="/"
              data-testid="back-home-btn"
              className="inline-block mt-8 bg-[#D4AF37] text-black px-7 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:-translate-y-0.5 transition-transform"
            >
              Back to home
            </Link>
          </motion.div>
        )}

        {state === "pending" && (
          <div className="text-center" data-testid="success-pending">
            <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto" />
            <p className="text-white mt-6 font-body">Payment is processing. We'll email your confirmation shortly.</p>
            <Link to="/" className="inline-block mt-6 text-[#D4AF37] uppercase tracking-[0.2em] text-sm link-underline">
              Back to home
            </Link>
          </div>
        )}

        {state === "error" && (
          <div className="text-center" data-testid="success-error">
            <div className="w-14 h-14 border border-white/20 flex items-center justify-center mx-auto mb-6">
              <X className="w-7 h-7 text-white/60" />
            </div>
            <p className="text-white font-body">We couldn't confirm this payment.</p>
            <Link to="/" className="inline-block mt-6 text-[#D4AF37] uppercase tracking-[0.2em] text-sm link-underline">
              Try booking again
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const Detail = ({ label, value, accent }) => (
  <div className="flex justify-between px-5 py-4">
    <span className="text-white/40 uppercase tracking-wide text-sm">{label}</span>
    <span className={`text-sm ${accent ? "text-[#D4AF37] font-semibold" : "text-white"}`}>{value}</span>
  </div>
);
